import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import { Card, CardTitle, Insight } from '../components/UI';

const F = 'system-ui,-apple-system,"Helvetica Neue",Arial,sans-serif';

const ACTION_COLORS = {
  waitlist_invited: '#2da06a',
  support_reply: '#314F5C',
  pass_purchased: '#e08c40',
  admin_login: '#9eb8c8',
  location_edited: '#6F8FA7',
  bulk_update: '#d97706',
};

const ACTION_LABELS = {
  waitlist_invited: 'Waitlist invite',
  support_reply: 'Support reply',
  pass_purchased: 'Pass purchased',
  admin_login: 'Admin login',
  location_edited: 'Location edited',
  bulk_update: 'Bulk update',
};

function timeAgo(ts) {
  if (!ts) return '';
  const ms = Date.now() - new Date(ts);
  if (ms < 60000)    return 'just now';
  if (ms < 3600000)  return Math.round(ms / 60000) + 'm ago';
  if (ms < 86400000) return Math.round(ms / 3600000) + 'h ago';
  return new Date(ts).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
}

function Skel({ h = 40, mb = 4 }) {
  return (
    <div style={{ height: h, borderRadius: 6, marginBottom: mb,
      background: 'linear-gradient(90deg,#edf2f5 25%,#f5f7f9 50%,#edf2f5 75%)',
      backgroundSize: '200% 100%', animation: 'shimmer 1.6s ease infinite' }} />
  );
}

export default function ActivityLog() {
  const [events,  setEvents]  = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter,  setFilter]  = useState('all');

  const load = useCallback(async () => {
    try {
      const { data } = await supabase.rpc('admin_get_activity', { p_limit: 100 });
      setEvents(data || []);
    } catch (_e) {}
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
    const ch = supabase.channel('activity-rt')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'admin_activity_log' }, load)
      .subscribe();
    return () => supabase.removeChannel(ch);
  }, [load]);

  const actionTypes = [...new Set(events.map(e => e.action_type).filter(Boolean))];
  const filtered = filter === 'all' ? events : events.filter(e => e.action_type === filter);

  return (
    <div style={{ fontFamily: F }}>
      <style>{`
        @keyframes shimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
        @keyframes pulse-dot {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>

      <Card>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
          <div>
            <CardTitle>Activity log</CardTitle>
            <div style={{ fontSize: '.65rem', color: '#9eb8c8', marginTop: -10 }}>
              {loading ? '\u2026' : events.length} total events
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <div style={{ width: 5, height: 5, borderRadius: '50%', background: '#2da06a',
              animation: 'pulse-dot 2s ease infinite' }} />
            <span style={{ fontSize: '.58rem', color: '#2da06a', fontWeight: 600 }}>Live</span>
          </div>
        </div>

        {/* Filter pills */}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 16 }}>
          {['all', ...actionTypes].map(type => (
            <button key={type} onClick={() => setFilter(type)}
              style={{ fontSize: '.62rem', padding: '3px 10px', borderRadius: 999,
                border: filter === type ? '1.5px solid #314F5C' : '.5px solid #d0dde7',
                background: filter === type ? '#314F5C' : '#fff',
                color: filter === type ? '#fff' : '#4a6878',
                cursor: 'pointer', fontWeight: filter === type ? 600 : 400 }}>
              {type === 'all' ? 'All' : (ACTION_LABELS[type] || type.replace(/_/g, ' '))}
            </button>
          ))}
        </div>

        {loading ? (
          [1,2,3,4,5,6,7,8].map(i => <Skel key={i} />)
        ) : filtered.length === 0 ? (
          <Insight type="info">No activity recorded yet.</Insight>
        ) : (
          filtered.map(item => (
            <div key={item.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 10,
              padding: '9px 0', borderBottom: '.5px solid rgba(49,79,92,.05)' }}>
              <div style={{ width: 7, height: 7, borderRadius: '50%', flexShrink: 0, marginTop: 5,
                background: ACTION_COLORS[item.action_type] || '#b0c8d4' }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '.73rem', fontWeight: 500, color: '#1a3040' }}>
                  {item.subject || (ACTION_LABELS[item.action_type] || item.action_type?.replace(/_/g, ' '))}
                </div>
                {item.detail && (
                  <div style={{ fontSize: '.63rem', color: '#9eb8c8', marginTop: 2,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {item.detail}
                  </div>
                )}
                {item.actor_email && (
                  <div style={{ fontSize: '.6rem', color: '#b0c8d4', marginTop: 1 }}>
                    {item.actor_email}
                  </div>
                )}
              </div>
              <div style={{ fontSize: '.62rem', color: '#c5d8e4', flexShrink: 0, textAlign: 'right' }}>
                <div>{timeAgo(item.created_at)}</div>
              </div>
            </div>
          ))
        )}
      </Card>
    </div>
  );
}
